import { useEffect, useState } from 'react';
import { ArrowUpRight, UtensilsCrossed } from 'lucide-react';
import kuickoMark from '@/assets/images/kuicko/kuicko-mark.png';
import { getKuickoDineNow } from '@/api/partnerRewards';
import { track } from '@/lib/analytics';

const KUICKO_URL = 'https://www.kuicko.in/';

/** One request per page load, shared by every card. */
let pending = null;
const loadDineNow = () => {
  if (!pending) pending = getKuickoDineNow().catch(() => null);
  return pending;
};

/**
 * "Dine Now on Kuicko" — restaurants on Kuicko's dine-in side and the
 * discounts they are running ("Up to 15% off"), promoted next to an event:
 * eat before or after the show. Restaurants with an offer lead. Hidden while
 * loading and when Kuicko has no restaurants to show.
 */
const DineNowPromo = ({ serviceId, className = '' }) => {
  const [data, setData] = useState(undefined);

  useEffect(() => {
    let live = true;
    loadDineNow().then((d) => live && setData(d));
    return () => {
      live = false;
    };
  }, []);

  if (!data || !data.restaurantCount) return null;

  const onOpen = (restaurant) => track('kuicko_dinenow_click', { serviceId, restaurant: restaurant ?? null });
  const shown = data.restaurants.slice(0, 4);

  return (
    <section
      className={`relative overflow-hidden rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 via-orange-50 to-white p-5 ${className}`}
      aria-labelledby="dine-now-title"
    >
      <UtensilsCrossed className="absolute -right-2 -top-2 h-16 w-16 text-rose-200/70" aria-hidden="true" />
      <div className="relative flex items-start gap-3">
        <img src={kuickoMark} alt="Kuicko" className="h-12 w-12 shrink-0 rounded-xl shadow-card" />
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-rose-600">Dine Now on Kuicko</p>
          <h3 id="dine-now-title" className="font-display text-base font-bold text-ink-900">
            {data.headline ? `${data.headline} on your table bill` : 'Dine out before or after the show'}
          </h3>
          <p className="mt-1 text-sm text-ink-600">
            {data.offerCount > 0
              ? `${data.offerCount} of ${data.restaurantCount} restaurants are running dine-in offers. Book a table or pay the bill on Kuicko to get it.`
              : `${data.restaurantCount} restaurants on Kuicko Dine Now — book a table or pay the bill in the app.`}
          </p>
        </div>
      </div>

      {/* inline-size containment: truncated addresses must not widen the page column. */}
      <ul className="relative mt-3 space-y-2 [contain:inline-size]">
        {shown.map((r) => (
          <li key={r.name}>
            <a
              href={KUICKO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onOpen(r.name)}
              className="flex items-center gap-3 rounded-xl border border-white bg-white/80 px-3 py-2 transition hover:border-rose-200 hover:bg-white"
            >
              {r.logoUrl ? (
                <img src={r.logoUrl} alt="" loading="lazy" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-100">
                  <UtensilsCrossed className="h-4 w-4 text-rose-500" aria-hidden="true" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink-900">{r.name}</span>
                {r.address && <span className="block truncate text-[11px] text-ink-500">{r.address}</span>}
              </span>
              {r.offer && (
                <span className="shrink-0 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 px-2 py-0.5 text-[11px] font-bold text-white">
                  {r.offer}
                </span>
              )}
            </a>
          </li>
        ))}
      </ul>

      <a
        href={KUICKO_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onOpen(null)}
        className="relative mt-3 inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:underline"
      >
        See all {data.restaurantCount} restaurants on Kuicko <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    </section>
  );
};

export default DineNowPromo;
